import { createLogger } from '@/lib/logger';
import type {
  ExportLinesBody,
} from '@octave/contracts';
import {
  AnalyticsFilters,
  UnusedLinesFilters,
} from '@/types/mobile-line.dto';
import {
  useMutation,
  } from '@tanstack/react-query';
import { toast } from 'sonner';
import { saveDownload } from '@/lib/download';
import {
  analyticsClient,
  lineClient,
  } from '@/lib/api/ts-rest';
import { useCurrentOrganization } from './useOrganization';

const log = createLogger('useLines');

interface ExportLinesParams {
  sites: string[];
  columns: ExportLinesBody['columns'];
  format: 'xlsx' | 'csv';
  delimiter: string;
  includeInactive: boolean;
}

export const useExportLines = () => {
  const { data: organization } = useCurrentOrganization();

  return useMutation<void, Error, ExportLinesParams>({
    mutationFn: async ({
      sites,
      columns,
      format,
      delimiter,
      includeInactive,
    }) => {
      if (!organization?.id) {
        throw new Error('Organization not loaded');
      }
      const res = await lineClient.exportLines({
        params: { organizationId: organization.id },
        body: { sites, columns, format, delimiter, includeInactive },
      });
      if (res.status !== 200) throw new Error('Export failed');
      saveDownload(
        res.body,
        `eiko-export-lines-${new Date().toISOString()}.${format}`,
      );
    },

    onError: (error) => {
      log.error('Error exporting lines', error);
      toast.error("Oups, une erreur est survenue lors de l'export");
    },
  });
};

export const useExportMobileLineUsage = (
  organizationId: string | undefined,
) => {
  return useMutation({
    mutationFn: async ({
      lineId,
      filters,
      format,
      delimiter = ';',
    }: {
      lineId: string;
      filters: AnalyticsFilters;
      format: 'xlsx' | 'csv';
      delimiter?: string;
    }) => {
      if (!organizationId) throw new Error('No organization selected');
      const res = await analyticsClient.exportLineUsage({
        params: { lineId },
        query: { organizationId },
        body: {
          startMonth: filters.startMonth,
          startYear: filters.startYear,
          startDay: filters.startDay,
          endMonth: filters.endMonth,
          endYear: filters.endYear,
          endDay: filters.endDay,
          productType: filters.productType,
          format,
          delimiter,
        },
      });
      if (res.status !== 200) throw new Error('Export failed');
      saveDownload(
        res.body,
        `eiko-export-consommation-${new Date().toISOString()}.${format}`,
      );
    },
    onError: (error) => {
      log.error('Error exporting mobile line usage', error);
      toast.error("Oups, une erreur est survenue lors de l'export");
    },
  });
};

export const useExportUnusedLines = (
  organizationId: string | undefined,
  siteId: string | undefined,
) => {
  return useMutation({
    mutationFn: async ({
      filters,
      format,
      delimiter = ';',
    }: {
      filters: UnusedLinesFilters;
      format: 'xlsx' | 'csv';
      delimiter?: string;
    }) => {
      if (!organizationId) throw new Error('No organization selected');
      const res = await analyticsClient.exportUnusedLines({
        params: { organizationId },
        body: { siteId, ...filters, format, delimiter },
      });
      if (res.status !== 200) throw new Error('Export failed');
      saveDownload(
        res.body,
        `eiko-export-lignes-inutilisees-${new Date().toISOString()}.${format}`,
      );
    },
  });
};
